import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, X, Send, Loader2, Users, AlertTriangle, Settings as SettingsIcon,
  Check, Plus, Trash2,
} from "lucide-react";

/**
 * NotifyLeadershipButton (Iter 76.18)
 *
 * One-click risk escalation. Opens a modal that previews the pre-populated
 * subject/body, lets the executive add an optional note, review the current
 * Board Alert distribution list, and (with permission) manage it inline.
 *
 * Presentation + one small backend endpoint (`/api/executive-ops/notify-leadership`
 * + `/board-alert/recipients`). Composes an HTML email server-side via the
 * existing Resend integration.
 */
export default function NotifyLeadershipButton({ risk, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="eib__btn eib__btn--warning"
        data-testid={testId || `eib-risk-${risk.id}-notify`}
      >
        <Mail className="w-3 h-3" /> Notify leadership
      </button>
      {open && (
        <NotifyLeadershipModal
          risk={risk}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function NotifyLeadershipModal({ risk, onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [editingRecipients, setEditingRecipients] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    api.get("/executive-ops/board-alert/recipients")
      .then(({ data }) => { if (!cancelled) setRecipients(data?.recipients || []); })
      .catch(() => { if (!cancelled) setRecipients([]); })
      .finally(() => { if (!cancelled) setLoadingRecipients(false); });
    return () => { cancelled = true; };
  }, []);

  const subject = `[HarborWell Board Alert] ${(risk.severity || "").toUpperCase()}: ${risk.title}`;

  const saveRecipients = async (nextList) => {
    setSaving(true);
    try {
      const { data } = await api.put("/executive-ops/board-alert/recipients",
                                       { recipients: nextList });
      setRecipients(data.recipients || []);
      toast.success("Board Alert recipients updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    const trimmed = (newRecipient || "").trim();
    if (!trimmed) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (recipients.map((r) => r.toLowerCase()).includes(trimmed.toLowerCase())) {
      toast.info("That address is already on the list");
      return;
    }
    saveRecipients([...recipients, trimmed]);
    setNewRecipient("");
  };

  const removeRecipient = (addr) => {
    if (!window.confirm(`Remove ${addr} from the Board Alert list?`)) return;
    saveRecipients(recipients.filter((r) => r !== addr));
  };

  const send = async () => {
    if (!recipients.length) {
      toast.error("Add at least one Board Alert recipient before sending");
      setEditingRecipients(true);
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/executive-ops/notify-leadership", {
        risk_id: risk.id,
        title: risk.title,
        severity: risk.severity,
        business_impact: risk.impact,
        program: risk.program,
        owner: risk.owner,
        recommended_action: risk.recommendedAction,
        deep_link: `${window.location.origin}${risk.link || ""}`,
        additional_note: note || null,
      });
      if (data.ok) {
        toast.success(`Sent to ${data.delivered.length} recipient${data.delivered.length === 1 ? "" : "s"}`);
        onClose();
      } else {
        toast.error(`Delivered ${data.delivered.length}, failed ${data.failed.length}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div
      className="eib-modal__backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="eib-notify-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="eib-modal" role="document">
        <header className="eib-modal__head">
          <div>
            <span className="eib-modal__eyebrow">
              <AlertTriangle className="w-3.5 h-3.5" /> Board Alert · Notify leadership
            </span>
            <h3 className="eib-modal__title">{risk.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="eib-modal__close"
            aria-label="Close"
            data-testid="eib-notify-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="eib-modal__body">
          {/* Subject preview */}
          <section className="eib-modal__section">
            <label className="eib-modal__label">Subject</label>
            <div className="eib-modal__subject" data-testid="eib-notify-subject">
              {subject}
            </div>
          </section>

          {/* Recipients */}
          <section className="eib-modal__section">
            <div className="eib-modal__section-head">
              <label className="eib-modal__label">
                <Users className="w-3.5 h-3.5" /> Recipients (Board Alert list)
              </label>
              <button
                type="button"
                onClick={() => setEditingRecipients((v) => !v)}
                className="eib-modal__inline-btn"
                data-testid="eib-notify-manage-recipients"
              >
                <SettingsIcon className="w-3 h-3" />
                {editingRecipients ? "Done" : "Manage"}
              </button>
            </div>
            {loadingRecipients ? (
              <div className="eib-modal__loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading recipients…
              </div>
            ) : recipients.length === 0 ? (
              <div className="eib-modal__empty" data-testid="eib-notify-recipients-empty">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  No Board Alert recipients configured yet. Click <strong>Manage</strong>{" "}
                  to add addresses.
                </span>
              </div>
            ) : (
              <ul className="eib-modal__chip-list" data-testid="eib-notify-recipients">
                {recipients.map((r) => (
                  <li key={r} className="eib-modal__chip">
                    <span>{r}</span>
                    {editingRecipients && (
                      <button
                        type="button"
                        onClick={() => removeRecipient(r)}
                        className="eib-modal__chip-remove"
                        aria-label={`Remove ${r}`}
                        data-testid={`eib-notify-remove-${r}`}
                        disabled={saving}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {editingRecipients && (
              <div className="eib-modal__add-row">
                <input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  placeholder="board@example.com"
                  className="eib-modal__input"
                  data-testid="eib-notify-new-recipient"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                  disabled={saving}
                />
                <button
                  type="button"
                  onClick={addRecipient}
                  disabled={saving || !newRecipient.trim()}
                  className="eib__btn eib__btn--outline"
                  data-testid="eib-notify-add-recipient"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Add
                </button>
              </div>
            )}
          </section>

          {/* Optional note */}
          <section className="eib-modal__section">
            <label className="eib-modal__label" htmlFor="eib-notify-note">
              Optional note to leadership
            </label>
            <textarea
              id="eib-notify-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context (optional) — e.g., specific concern or requested action."
              className="eib-modal__textarea"
              rows={3}
              maxLength={800}
              data-testid="eib-notify-note"
            />
            <div className="eib-modal__hint">{note.length}/800</div>
          </section>

          {/* Preview details */}
          <section className="eib-modal__section">
            <label className="eib-modal__label">Email body preview</label>
            <div className="eib-modal__preview" data-testid="eib-notify-preview">
              <div className="eib-modal__preview-row">
                <span>Severity</span>
                <strong style={{
                  color: risk.severity === "critical" ? "#991B1B"
                       : risk.severity === "high" ? "#92400E"
                       : "#1E40AF",
                }}>{(risk.severity || "").toUpperCase()}</strong>
              </div>
              <div className="eib-modal__preview-row">
                <span>Program</span><strong>{risk.program || "—"}</strong>
              </div>
              <div className="eib-modal__preview-row">
                <span>Owner</span><strong>{risk.owner || "—"}</strong>
              </div>
              <div className="eib-modal__preview-row">
                <span>Recommended action</span><strong>{risk.recommendedAction || "—"}</strong>
              </div>
              {risk.impact && (
                <div className="eib-modal__preview-body">
                  <em>Business impact:</em> {risk.impact}
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="eib-modal__foot">
          <button
            type="button"
            onClick={onClose}
            className="eib__btn eib__btn--outline"
            data-testid="eib-notify-cancel"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            className="eib__btn eib__btn--primary"
            data-testid="eib-notify-send"
            disabled={sending || loadingRecipients}
          >
            {sending
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              : <><Send className="w-4 h-4" /> Send to {recipients.length} recipient{recipients.length === 1 ? "" : "s"}</>
            }
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
